import React, { useState, useMemo, useCallback } from "react";

import { DataSection, SubSection } from "../../../components/data-components/sections/DataSections";
import { Dropdown, DropdownOption } from "../../../components/dropdown/Dropdown";
import FormField from "../../../components/form-field/FormField";
import { IntegerNumberValidatorModel } from "../../../services/form/validators/number/IntegerNumberValidator";
import { RequiredValidatorModel } from "../../../services/form/validators/required/RequiredValidator";
import { EmailValidatorModel } from "../../../services/form/validators/email/EmailValidator";
import SuggestionBox from "../../../components/suggestion-box/SuggestionBox";
import { AddressEntity } from "../../../services/address/AddressCleansingService";

interface EditTextFieldProps {
  id: string;
  labelKey: string;
  name: string;
  placeholderKey?: string;
  fieldName: string;
  required?: boolean;
  readonly?: boolean;
  isEmail?: true;
}

interface DisplayTextFieldProps {
  id: string;
  labelKey: string;
  value: string;
}

interface DropdownFieldProps {
  id: string;
  labelKey: string;
  required?: boolean;
  readonly?: boolean;
  fieldName: string;
  name: string;
  options: Array<any>;
  setSelectedItem: (value: any) => void | undefined;
}

interface SuggestionFieldProps {
  id: string;
  labelKey: string;
  placeholderKey: string;
  required?: boolean;
  isNumber?: boolean;
  fieldName: string;
  fieldValue: string;
  performSearch: (searchText: string) => Promise<AddressEntity[]>;
  searchTextChanged: (searchText: string) => void;
  selectedItemChanged: (item: AddressEntity) => void;
  clearCurrentItem: () => void;
}

export function EditTextField(props: EditTextFieldProps) {
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));
  const [emailValidator] = useState(() =>
    EmailValidatorModel.create({ isRequired: required ?? false })
  );
  const { id, labelKey, readonly, fieldName, name, required, placeholderKey, isEmail } = props;
  return (
    <FormField
      id={id}
      required={required}
      className="input"
      labelKey={labelKey}
      readOnly={readonly}
      component="input"
      fieldName={fieldName}
      fieldValue={name}
      validator={isEmail ? emailValidator : required ? requiredValidator : undefined}
      placeholderKey={placeholderKey}
      maxLength={100}
      errorName={fieldName}
    />
  );
}

export function DisplayTextField(props: DisplayTextFieldProps) {
  const { id, labelKey, value } = props;
  return <FormField id={id} labelKey={labelKey} readOnly fieldValue={value} />;
}

export function EditIntegerField(props: EditTextFieldProps) {
  const [requiredIntegerNumberValidator] = useState(() =>
    IntegerNumberValidatorModel.create({
      isRequired: true,
    })
  );
  const { id, labelKey, readonly, fieldName, name, placeholderKey } = props;

  return (
    <FormField
      id={id}
      required
      className="input"
      labelKey={labelKey}
      readOnly={readonly}
      component="input"
      fieldName={fieldName}
      fieldValue={name}
      validator={requiredIntegerNumberValidator}
      placeholderKey={placeholderKey}
      maxLength={100}
      errorName={fieldName}
    />
  );
}

export function EditDropdownField(props: DropdownFieldProps) {
  const { id, labelKey, required, readonly, fieldName, name, options, setSelectedItem } = props;

  const optionsList: DropdownOption[] = useMemo(() => {
    return options.map((option: any) => {
      return {
        id: option.id,
        name: option.name,
      };
    });
  }, [options]);

  return (
    <FormField
      id={id}
      labelKey={labelKey}
      required={required}
      readOnly={readonly}
      className="select"
      fieldType="select"
      fieldName={fieldName}
      fieldValue={name}
      errorName={fieldName}
      children={() => (
        <Dropdown
          selectedOption={optionsList.find((o) => o.id === name) ?? { id: "", name: "" }}
          options={optionsList}
          fieldName={fieldName}
          setFieldValue={(field: string, value: any) => setSelectedItem(value.id)}
        />
      )}
    />
  );
}

export function EditDropdownSuggestionField(props: SuggestionFieldProps) {
  const {
    id,
    labelKey,
    placeholderKey,
    required,
    isNumber,
    fieldName,
    fieldValue,
    performSearch,
    searchTextChanged,
    selectedItemChanged,
    clearCurrentItem,
  } = props;

  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));
  const [numberValidator] = useState(() =>
    IntegerNumberValidatorModel.create({ isRequired: required ?? false })
  );
  const fieldValidator = isNumber ? numberValidator : required ? requiredValidator : undefined;

  const handleSearchChange = useCallback(
    async (searchText: string): Promise<AddressEntity[]> => {
      try {
        return await performSearch(searchText);
      } catch {
        return [];
      }
    },
    [performSearch]
  );

  return (
    <FormField
      id={id}
      labelKey={labelKey}
      fieldName={fieldName}
      fieldValue={fieldValue}
      required={required}
      validator={fieldValidator}
      errorName={fieldName}
      children={() => (
        <SuggestionBox
          onSearchTextChange={handleSearchChange}
          searchTextChanged={searchTextChanged}
          selectedItemChanged={(item: any) => selectedItemChanged(item)}
          clearCurrentItem={clearCurrentItem}
          defaultInput={fieldValue}
          placeholderKey={placeholderKey}
          freeSolo
        />
      )}
    />
  );
}

interface MessageFieldProps {
  message: string;
  placeholderKey: string;
}

export function EditMessageSection(props: MessageFieldProps) {
  const { message, placeholderKey } = props;
  return (
    <DataSection labelKey="general.message">
      <SubSection>
        <FormField
          id="message"
          className="textarea"
          labelKey={"general.message"}
          fieldName="message"
          fieldValue={message}
          component="textarea"
          placeholderKey={placeholderKey}
          maxLength={1000}
        />
      </SubSection>
    </DataSection>
  );
}
